import { EventPanelFormType } from '@mark43/cad-api';
import { FieldPathValue } from 'react-hook-form';
import { FederatedSearchArticle, FederatedSearchFirearm } from 'mark43-federated-search';

/**
 * Builds the Federated Search (dexNext) prefill values for a firearm.
 *
 * The firearm serial number must be keyed `GunSerialNumber` — that is the field the
 * firearm query form registers. Sending `SerialNumber` (which is the article serial key)
 * is silently dropped by `setQueryFormValues`, leaving the serial field empty.
 */
export function getFirearmDexNextPrefillValues(values: {
    serialNumber?: string | null;
    firearmMake?: string;
    caliber?: string;
    gunType?: string;
    freeText?: string | null;
}): Omit<FederatedSearchFirearm, 'entityType'> {
    return {
        GunSerialNumber: values.serialNumber ?? undefined,
        FirearmMake: values.firearmMake,
        Caliber: values.caliber,
        GunType: values.gunType,
        FreeText: values.freeText ?? undefined,
    };
}

/**
 * Builds the Federated Search (dexNext) prefill values for an other item (article).
 * Articles legitimately use the `SerialNumber` key.
 */
export function getOtherItemDexNextPrefillValues(values: {
    serialNumber?: string | null;
    articleModel?: string | null;
    articleCategory?: string;
    freeText?: string | null;
}): Omit<FederatedSearchArticle, 'entityType'> {
    return {
        SerialNumber: values.serialNumber ?? undefined,
        ArticleModel: values.articleModel ?? undefined,
        ArticleCategory: values.articleCategory,
        FreeText: values.freeText ?? undefined,
    };
}

export function getDefaultFirearm(): FieldPathValue<EventPanelFormType, 'firearms.0'> {
    return {
        alterationIndicated: false,
        caliber: null,
        description: null,
        firearmMake: null,
        firearmType: null,
        id: null,
        contextId: null,
        identifier: null,
        identifierType: null,
        identifierTypeOtherDescription: null,
        isPersisted: false,
        isRemoved: false,
        model: null,
        registrationNumber: null,
        serialNumber: null,
        eventInfo: null,
        purposeCode: null,
        queryInDex: false,
        imageIndicator: null,
    };
}

export function getDefaultDrug(): FieldPathValue<EventPanelFormType, 'drugs.0'> {
    return {
        description: null,
        drugCategory: null,
        id: null,
        contextId: null,
        identifier: null,
        identifierType: null,
        identifierTypeOtherDescription: null,
        isPersisted: false,
        isPrecursorChemical: false,
        isRemoved: false,
        measurementUnits: null,
        // @ts-expect-error need to use '' for NumberInput
        quantity: '',
        eventInfo: null,
        purposeCode: null,
        queryInDex: false,
        imageIndicator: null,
    };
}

export function getDefaultOtherItem(): FieldPathValue<EventPanelFormType, 'otherItems.0'> {
    return {
        description: null,
        color: null,
        id: null,
        contextId: null,
        identifier: null,
        identifierType: null,
        identifierTypeOtherDescription: null,
        isPersisted: false,
        itemCategory: null,
        isRemoved: false,
        make: null,
        model: null,
        // @ts-expect-error need to use '' for NumberInput
        quantity: '',
        serialNumber: null,
        size: null,
        eventInfo: null,
        purposeCode: null,
        queryInDex: false,
        imageIndicator: null,
    };
}

export const getPropertyNameForControl = (baseKey: string): string => {
    if (baseKey === 'firearms' || baseKey === 'mergedFirearms') return 'Firearm';
    if (baseKey === 'drugs' || baseKey === 'mergedDrugs') return 'Drugs';
    if (baseKey === 'otherItems' || baseKey === 'mergedOtherItems') return 'Other';
    return 'Unknown';
};
